import { createContext, useContext, useEffect } from 'react';
import { generateAnalyticsCb as generateAnalyticsCallback, onTrack } from '@cld/analytics';

export type AnalyticsPlatform = 'website' | 'console' | undefined;
type TransformationsType = 'dynamic' | 'named' | 'all';

export type AnalyticsCategory =
  | 'transformations side menu'
  | 'cookbook grid'
  | 'named transformations search bar'
  | `${TransformationsType} transformations grid`
  | `cookbook ${'search bar' | 'grid' | 'recipe modal'}`
  | string;

export interface AnalyticsData {
  eventCategory: AnalyticsCategory;
  eventAction: string;
  eventLabel: string;
}

export type TrackFunction = (event: AnalyticsData) => void;

const AnalyticsContext = createContext<AnalyticsCategory | undefined>(undefined);
const AnalyticsPlatformContext = createContext<AnalyticsPlatform | undefined>(undefined);

const useAnalyticsContext = () => {
  const eventCategory = useContext(AnalyticsContext);
  if (eventCategory === undefined) {
    throw new Error('Missing context with analytics settings (AnalyticsProvider)');
  }
  return eventCategory;
};

const useAnalyticsPlatformContext = () => {
  const platform = useContext(AnalyticsPlatformContext);
  if (platform === undefined) {
    throw new Error('Missing context with analytics platform (AnalyticsPlatformProvider)');
  }
  return platform;
};

export const useAnalyticsTrack = (track?: TrackFunction) =>
  useEffect(() => {
    if (track) {
      onTrack(track);
    }
  }, [track]);

export const useAnalytics = (customCategory?: AnalyticsCategory) => {
  const eventCategory = useAnalyticsContext();
  return (eventAction: AnalyticsData['eventAction'], eventLabel: AnalyticsData['eventLabel']) => {
    return generateAnalyticsCallback({ eventLabel, eventAction, eventCategory: customCategory || eventCategory })();
  };
};

export const usePlatformAnalytics = (customCategory?: AnalyticsCategory) => {
  const platform = useAnalyticsPlatformContext();
  const eventCategory = useAnalyticsContext();

  return useAnalytics(`${platform} ${customCategory || eventCategory}`);
};

export const AnalyticsProvider = AnalyticsContext.Provider;
export const AnalyticsPlatformProvider = AnalyticsPlatformContext.Provider;
